// DEPENDENCIES ---------------------------------------------------------------- //

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

import { Arinvoices } from '@gql/base-types';

import { Box, Tooltip, Typography } from '@mui/material';

import { BiSolidAdjust, BiSolidBadgeDollar, BiSolidCheckCircle, BiSolidCommentError } from 'react-icons/bi';

import { getMonetaryValue } from '@services/formatService';
import { buildAmountObject } from '@features/invoices/services/calcService';
import { isChargeBasedInvoiceType } from '@features/invoices/services/invoiceType';
import { listedInvoiceAmountDue, type ListedArgroup } from '@hopdrive/sdk-ar';
import { AMOUNT_DUE_LOADING_TEXT } from '@features/invoices/services/calcServiceCharges';
import { AMOUNT_DUE_UNAVAILABLE_TEXT } from '@features/invoices/hooks/useInvoiceAmountDue';
import { allTokens, MONO_FONT } from '../../../lib/tokens';
import InvoiceStatusChip from '@features/invoices/components/InvoiceStatusChip';

// TYPES ---------------------------------------------------------------- //

/**
 * A charge invoice's rows from getInvoiceSidebarAmountDue, which its Amount Due
 * is worked out from: 'loading' until the sidebar has them, and 'unavailable'
 * when that read failed.
 */
export type SidebarAmountDueArgroups = ReadonlyArray<ListedArgroup> | 'loading' | 'unavailable';

type InvoiceSidebarItemProps = {
  invoice?: Arinvoices;
  selectedInvoiceId?: number;
  amountDueArgroups?: SidebarAmountDueArgroups;
};

// CONSTANTS ---------------------------------------------------------------- //

/** What the detail pane restores when the dealer comes back to this invoice. */
const STORED_INVOICE_ID = 'invoiceId';
const STORED_INVOICE_CUSTOMER_ID = 'invoiceCustomerId';
const STORED_INVOICE_FOLD_ID = 'invoiceFoldId';
const NO_STORED_ID = '0';

// COMPONENT ---------------------------------------------------------------- //

/**
 * A real link rather than a div that calls `navigate`, because only a link gives a
 * dealer ctrl-click, middle-click, "open in new tab" and an address on hover.
 *
 * Selection is a rail and a tint, not a primary fill, because the primary colour is
 * reserved for a call to action.
 */
const InvoiceSidebarItem = ({ invoice, selectedInvoiceId, amountDueArgroups = 'loading' }: InvoiceSidebarItemProps) => {
  const isChargeBasedInvoice = isChargeBasedInvoiceType(invoice?.invoice_type);

  // invoice_type alone decides which rows are the truth. A charge invoice shows sdk-ar's Amount
  // Due worked out from the rows the sidebar already holds, with no reads. It is the detail
  // pane's figure unless a credit or charge for one of the invoice's moves sits on another
  // invoice. A standard or rideshare invoice shows its armoves' figure.
  const chargeGroups = isChargeBasedInvoice && typeof amountDueArgroups !== 'string' ? amountDueArgroups : null;
  const chargeAmountDue = useMemo(() => (chargeGroups ? listedInvoiceAmountDue(chargeGroups) : null), [chargeGroups]);
  const amount = isChargeBasedInvoice ? null : buildAmountObject(invoice);
  const dueText = !isChargeBasedInvoice
    ? getMonetaryValue(amount?.due)
    : chargeAmountDue !== null
      ? getMonetaryValue(chargeAmountDue)
      : amountDueArgroups === 'unavailable'
        ? AMOUNT_DUE_UNAVAILABLE_TEXT
        : AMOUNT_DUE_LOADING_TEXT;

  const isSelected = selectedInvoiceId === invoice?.id;
  const billedThroughArmoves = !isChargeBasedInvoice;
  const partlyPaid = billedThroughArmoves && amount?.disputed <= 0 && amount?.paid > 0 && amount?.paid <= amount?.total && amount?.due > 0;
  const fullyPaid =
    billedThroughArmoves &&
    ((amount?.disputed <= 0 && amount?.paid >= 0 && amount?.paid >= amount?.total) || (amount?.due != null && amount.due <= 0));

  // The detail pane reads these back when the dealer returns to the invoice,
  // so they are written on the way out rather than left to the destination.
  const rememberInvoice = () => {
    localStorage.setItem(STORED_INVOICE_ID, invoice?.id?.toString() || NO_STORED_ID);
    localStorage.setItem(STORED_INVOICE_CUSTOMER_ID, invoice?.customer?.id?.toString() || NO_STORED_ID);
    localStorage.setItem(STORED_INVOICE_FOLD_ID, NO_STORED_ID);
  };

  return (
    <Box
      component={Link}
      to={`/invoices/${invoice?.id}`}
      onClick={rememberInvoice}
      aria-current={isSelected ? 'page' : undefined}
      sx={{
        display: 'block',
        px: 2,
        py: 1.5,
        textDecoration: 'none',
        color: 'text.primary',
        // The rail marks the row without taking the surface the amount and the
        // status chip have to stay readable on.
        borderLeft: `3px solid ${isSelected ? allTokens.palette.secondaryAccent.main : 'transparent'}`,
        bgcolor: isSelected ? allTokens.palette.secondaryAccent[50] : 'background.paper',
        transition: 'background-color 150ms',
        '&:hover': { bgcolor: isSelected ? allTokens.palette.secondaryAccent[50] : 'action.hover' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3 }}>
          {dayjs.utc(dayjs(invoice?.start_datetime)).format('MM/DD/YY')} –{' '}
          {dayjs.utc(dayjs(invoice?.end_datetime)).format('MM/DD/YY')}
        </Typography>

        <Tooltip title='Amount due' placement='top'>
          {/* Heavier than the dates beside it, so a column of amounts can be
              read down the list rather than across each row. */}
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{dueText}</Typography>
        </Tooltip>
      </Box>

      <Typography sx={{ mt: 0.75, fontSize: 14, lineHeight: 1.3 }}>{invoice?.customer?.name}</Typography>

      <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Tooltip title='HopDrive invoice id' placement='top'>
          <Typography sx={{ fontFamily: MONO_FONT, fontSize: 13, color: allTokens.palette.secondaryAccent[700] }}>
            #{invoice?.id}
          </Typography>
        </Tooltip>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          {billedThroughArmoves && amount?.discounted > 0 ? (
            <Tooltip title='Has discounts' placement='top'>
              <span>
                <BiSolidBadgeDollar size={16} />
              </span>
            </Tooltip>
          ) : null}

          {billedThroughArmoves && amount?.disputed > 0 ? (
            <Tooltip title='Has disputes' placement='top'>
              <span>
                <BiSolidCommentError size={16} />
              </span>
            </Tooltip>
          ) : null}

          {partlyPaid ? (
            <Tooltip title='Partially paid' placement='top'>
              <span>
                <BiSolidAdjust size={16} />
              </span>
            </Tooltip>
          ) : null}

          {fullyPaid ? (
            <Tooltip title='Fully paid' placement='top'>
              <span style={{ color: allTokens.palette.success.main }}>
                <BiSolidCheckCircle size={16} />
              </span>
            </Tooltip>
          ) : null}

          <InvoiceStatusChip status={invoice?.status} />
        </Box>
      </Box>
    </Box>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default InvoiceSidebarItem;
